import { useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Download, FileSpreadsheet, LogOut, Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'wouter'
import { Card, ErrorBox, PageHead, Spinner } from '../components/ui'
import { api, download } from '../lib/api'
import { ago, initials } from '../lib/format'
import { keys, useMe } from '../lib/hooks'

export function Workspaces() {
  const me = useMe()
  const qc = useQueryClient()
  const [, navigate] = useLocation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const building = me.data?.workspaces.some(w => w.status === 'building')

  useEffect(() => {
    if (!building) return
    const t = setInterval(() => void qc.invalidateQueries({ queryKey: keys.me }), 1500)
    return () => clearInterval(t)
  }, [building, qc])

  const sample = async () => {
    setBusy(true)
    setError(null)
    try {
      const r = await api.post<{ workspaceId: string }>('/workspaces/sample')
      await qc.invalidateQueries({ queryKey: keys.me })
      navigate(`/w/${r.workspaceId}`)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  const logout = async () => {
    await api.post('/auth/logout')
    qc.clear()
    navigate('/login')
  }

  return (
    <div className="page" style={{ maxWidth: 960 }}>
      <div className="spread" style={{ marginBottom: 28 }}>
        <div className="brand-name">
          summation <span>plan</span>
        </div>
        <div className="row">
          <span className="small muted">{me.data?.user.email}</span>
          <button className="btn ghost sm" onClick={logout}>
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </div>
      <PageHead
        title="Workspaces"
        sub="Each workspace is one operating model: its dimensions, measures, formulas and tests in git, and its plan data alongside."
        right={
          <>
            <button className="btn" onClick={sample} disabled={busy}>
              {busy ? <Spinner /> : <Sparkles size={14} />} New sample workspace
            </button>
            <Link href="/new" className="btn primary">
              <FileSpreadsheet size={14} /> Import a workbook
            </Link>
          </>
        }
      />
      {error ? <ErrorBox error={error} /> : null}
      <div className="stack">
        {(me.data?.workspaces ?? []).map(w => (
          <Card key={w.id}>
            <div className="spread card-pad">
              <div className="row" style={{ gap: 14 }}>
                <span className="ws-mark" style={{ width: 36, height: 36, fontSize: 13 }}>
                  {initials(w.name)}
                </span>
                <div>
                  <div className="strong">{w.name}</div>
                  <div className="small muted">
                    {w.role} · created {ago(w.createdAt)} ·{' '}
                    {w.origin.startsWith('import:')
                      ? `imported from ${w.origin.slice(7)}`
                      : w.origin === 'sample:halcyon'
                        ? 'sample'
                        : w.origin}
                  </div>
                  {w.status === 'failed' && (
                    <div className="issue" style={{ marginTop: 6 }}>
                      {w.error ?? 'build failed'}
                    </div>
                  )}
                </div>
              </div>
              {w.status === 'ready' ? (
                <Link href={`/w/${w.id}`} className="btn sm">
                  Open <ArrowRight size={13} />
                </Link>
              ) : w.status === 'building' ? (
                <span className="row small muted">
                  <Spinner /> building…
                </span>
              ) : (
                <span className="pill red">failed</span>
              )}
            </div>
          </Card>
        ))}
        {me.data?.workspaces.length === 0 && (
          <Card>
            <div className="card-pad stack">
              <div className="strong">No workspaces yet</div>
              <div className="muted small">
                Import a planning workbook (.xlsx — Google Sheets works via File → Download → .xlsx), or start from the sample.
              </div>
            </div>
          </Card>
        )}
      </div>
      <div className="notice row" style={{ marginTop: 28 }}>
        <FileSpreadsheet size={15} />
        <span className="grow">
          Want to see the importer on a realistic file first? Download the sample budget workbook, then import it.
        </span>
        <button className="btn sm" onClick={() => void download('/sample/workbook', 'sample.xlsx')}>
          <Download size={13} /> Sample workbook
        </button>
      </div>
    </div>
  )
}
